import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import LZString from 'lz-string';
import { useSharedData } from './SharedDataContext';

// Global character limit for all notes combined
export const GLOBAL_CHARACTER_LIMIT = 2000;

interface NotesContextType {
  trackNotes: Record<string, string>;
  addNote: (fileId: string, note: string) => void;
  getNote: (fileId: string) => string;
  hasNotes: boolean;
  generateShareableURL: () => string;
  getTotalCharacterCount: () => number;
  getRemainingCharacters: () => number;
  canAddCharacters: (additionalChars: number) => boolean;
  migrateNotesFromTrackNumbers: (files: Array<{ id: string }>) => void;
}

const NotesContext = createContext<NotesContextType | undefined>(undefined);

// Create a persistent object to store notes by file ID (similar to display names)
const persistentNotes: Record<string, string> = {};

export const NotesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use ref to maintain the same object reference
  const trackNotesRef = useRef(persistentNotes);
  const [, forceUpdate] = useState({});
  const { shareData } = useSharedData();

  // Load notes from shared data when available
  // Shared data has notes keyed by track position ("1", "2", "3")
  // We'll store them with track- prefix for migration to file IDs
  useEffect(() => {
    if (shareData?.notes) {
      console.log('[Notes] Loading notes from shareData:', shareData.notes);
      // Clear existing notes before loading new ones
      Object.keys(trackNotesRef.current).forEach(key => {
        delete trackNotesRef.current[key];
      });
      
      // Store shared notes with track- prefix - will be migrated when files load
      Object.entries(shareData.notes).forEach(([key, value]) => {
        // Check if key is a simple number (track position format)
        if (/^\d+$/.test(key)) {
          // Store with track- prefix - will be migrated when files load
          trackNotesRef.current[`track-${key}`] = value as string;
          console.log(`[Notes] Stored track-${key}: "${value}"`);
        } else {
          // Key looks like a file ID (legacy format or already migrated)
          trackNotesRef.current[key] = value as string;
          console.log(`[Notes] Stored fileId-${key}: "${value}"`);
        }
      });
      
      console.log('[Notes] All stored notes:', Object.keys(trackNotesRef.current));
      forceUpdate({}); // Force a re-render
    }
  }, [shareData]);

  // Migrate notes from track numbers to file IDs when files are loaded
  // Similar to how display names are migrated
  const migrateNotesFromTrackNumbers = (files: Array<{ id: string }>) => {
    if (files.length === 0) return;
    
    console.log('[Notes] Starting migration for', files.length, 'files');
    const migrated = new Set<string>();
    
    files.forEach((file, index) => {
      const trackNumber = index + 1;
      const trackKey = `track-${trackNumber}`;
      const trackNote = trackNotesRef.current[trackKey];
      
      if (trackNote) {
        // Migrate note from track number to file ID
        trackNotesRef.current[file.id] = trackNote;
        delete trackNotesRef.current[trackKey];
        migrated.add(trackKey);
        console.log(`[Notes] Migrated note from ${trackKey} to fileId ${file.id}`);
      }
    });
    
    // Clean up any remaining track- keys that weren't migrated
    Object.keys(trackNotesRef.current).forEach(key => {
      if (key.startsWith('track-') && !migrated.has(key)) {
        delete trackNotesRef.current[key];
      }
    });
    
    if (migrated.size > 0) {
      console.log(`[Notes] Migration complete, migrated ${migrated.size} notes`);
      forceUpdate({}); // Force a re-render
    }
  };

  const addNote = (fileId: string, note: string) => {
    // Update the persistent notes object
    trackNotesRef.current[fileId] = note;
    forceUpdate({}); // Force a re-render
  };

  const getNote = (fileId: string) => {
    return trackNotesRef.current[fileId] || '';
  };

  const hasNotes = Object.keys(trackNotesRef.current).length > 0;

  const generateShareableURL = () => {
    const currentUrl = new URL(window.location.href);
    const shareData = {
      notes: trackNotesRef.current,
    };
    
    const compressedData = LZString.compressToEncodedURIComponent(JSON.stringify(shareData));
    currentUrl.searchParams.set('data', compressedData);
    
    return currentUrl.toString();
  };

  const getTotalCharacterCount = () => {
    return Object.values(trackNotesRef.current).reduce((total, note) => {
      // Strip HTML tags to get plain text character count
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = note;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      return total + plainText.length;
    }, 0);
  };

  const getRemainingCharacters = () => {
    return Math.max(0, GLOBAL_CHARACTER_LIMIT - getTotalCharacterCount());
  };

  const canAddCharacters = (additionalChars: number) => {
    return getTotalCharacterCount() + additionalChars <= GLOBAL_CHARACTER_LIMIT;
  };

  return (
    <NotesContext.Provider 
      value={{ 
        trackNotes: trackNotesRef.current, 
        addNote, 
        getNote, 
        hasNotes, 
        generateShareableURL,
        getTotalCharacterCount,
        getRemainingCharacters,
        canAddCharacters,
        migrateNotesFromTrackNumbers
      }}
    >
      {children}
    </NotesContext.Provider>
  );
};

export const useNotes = () => {
  const context = useContext(NotesContext);
  if (context === undefined) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
}; 